// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";
import cx from "classnames";

// internal-deps
import { Tick } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// interfaces
import { PaymentMethodDef } from "@payment-ui/interfaces";

// widgets
import { PaymentMethodName } from "@payment-ui/widgets/payment-method-name";
import { PaymentMethodIcon } from "@payment-ui/widgets/payment-method-icon";
import { Alert } from "@payment-ui/widgets/alert";
import { ArrowSvgIcon } from "@payment-ui/widgets/arrow-svg-icon";

// helpers
import {
    getPaymentMethodPromoMsg,
    getPaymentMethodIconUrl,
    getPaymentMethodDisplayName,
    getPaymentMethodDiscount,
    isPaymentMethodEnabled,
    isMMTPayment,
} from "@payment-ui/helpers/Payment";

// testids
import { ListViewTestIdsType } from "@swiggy-private/payment-test-ids";

import styles from "./ListView.v4.scss";

export interface ListViewProps {
    methodData: PaymentMethodDef;
    onPayButtonClick: VoidFunction;
    onClick: VoidFunction;
    isMethodEnabled: boolean;
    selected: boolean;
    payCtaMsg: string;
    testIds: ListViewTestIdsType;
    isThirdPartyIntentApp?: boolean;
    showArrow?: boolean;
    balance?: string;
}

function PaymentMethodListView(props: ListViewProps): ReactElement {
    const {
        methodData,
        onClick,
        onPayButtonClick,
        isMethodEnabled,
        testIds,
        selected,
        payCtaMsg,
        isThirdPartyIntentApp,
        showArrow = false,
        balance,
    } = props;
    const iconUrl = useMemo(
        () => getPaymentMethodIconUrl(methodData, isThirdPartyIntentApp),
        [methodData, isThirdPartyIntentApp],
    );
    const displayName = useMemo(() => getPaymentMethodDisplayName(methodData), [methodData]);
    const promoMsg = useMemo(() => getPaymentMethodPromoMsg(methodData), [methodData]);
    const discount = useMemo(() => getPaymentMethodDiscount(methodData), [methodData]);
    const isNewBadgeEnabled = useMemo(() => methodData.meta?.newBadgeEnabled, [methodData]);
    const isPaymenMethodMmt = isMMTPayment(methodData);

    const containerClass = useMemo(
        () =>
            cx(styles.paymentMethod, {
                [styles.paymentMethodDisabled]: !isMethodEnabled,
                [styles.paymentMethodMmt]: isPaymenMethodMmt,
            }),
        [isMethodEnabled, isPaymenMethodMmt],
    );

    const onContainerClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            if (!isMethodEnabled) {
                return;
            }
            onClick();
        },
        [isMethodEnabled, onClick],
    );

    const onPayClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onPayButtonClick();
        },
        [onPayButtonClick],
    );

    return (
        <div className={containerClass} onClick={onContainerClick} data-testid={testIds.CONTAINER}>
            <div className={styles.paymentMethodContainer}>
                <PaymentMethodIcon
                    src={iconUrl}
                    data-testid={testIds.ICON}
                    isThirdPartyDisabledIntentApp={isThirdPartyIntentApp && !isPaymentMethodEnabled(methodData)}
                />
                <PaymentMethodName
                    displayName={displayName}
                    promoMsg={promoMsg}
                    enabled={isMethodEnabled}
                    discount={discount}
                    isNewBadgeEnabled={isNewBadgeEnabled}
                    data-testid={testIds.NAME}
                >
                    {!isMethodEnabled && (
                        <Alert
                            className={styles.paymentMethodDisable}
                            message={props.methodData.meta.disableMessage}
                            data-testid={testIds.DISABLE_MESSAGE}
                        />
                    )}
                </PaymentMethodName>
                {balance && (
                    <div data-testid={testIds?.BALANCE} className={styles.paymentMethodBalance}>
                        {balance}
                    </div>
                )}
                {showArrow && <ArrowSvgIcon className={styles.paymentMethodArrow} />}
                {isMethodEnabled && !showArrow && (
                    <Tick
                        selected={selected}
                        className={styles.paymentMethodContainerTick}
                        data-testid={testIds.TICK}
                    />
                )}
            </div>
            {selected && isMethodEnabled && (
                <div className={styles.paymentMethodPayWrapper}>
                    <SuperButton
                        variant="fullwidth"
                        buttonType="specialPositive"
                        buttonSize="medium"
                        isDisabled={!isMethodEnabled}
                        onClick={onPayClick}
                        className={styles.paymentMethodPayButton}
                        data-testid={testIds.PAY_BUTTON}
                    >
                        {payCtaMsg}
                    </SuperButton>
                </div>
            )}
        </div>
    );
}

export default memo(PaymentMethodListView);
